import { API_DOMAIN } from "@launchos/shared-utils/env";
import { collectionExists, createCollection } from "./crm";
import { has, keys } from "lodash";
import { forgetExpiry, rememberExpiry } from "../Login/loginCheck";

/**
 * Logs in a user with the provided email and password.
 * @param {Object} credentials - The user's login credentials.
 * @param {string} credentials.email - The user's email.
 * @param {string} credentials.password - The user's password.
 * @returns {Promise<Object|string>} - A promise that resolves to the user data if successful, or an error message if unsuccessful.
 */
// a normal sign-in or sign-out ends any leftover admin "log in as" visit (its orange bar stayed up
// the next day otherwise — Claire, Oct 28 scrum)
const endAdminVisit = () => ["adminToken", "adminUser", "adminViewing"].forEach((k) => localStorage.removeItem(k));

export const loginUser = async ({ email, password, clerkToken, userId }) => {
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");

  const raw = JSON.stringify({
    email,
    password,
    clerkToken,
    userId,
  });

  try {
    const response = await fetch(`${API_DOMAIN}/sessions`, {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    });

    const data = await response.json();

    if (data.error) {
      return data.error;
    } else {
      // check if the user has a crm table
      const { token } = data;
      const collectionId = "crm";
      const resp = await collectionExists(collectionId, { token });

      if (!resp) {
        // create the collection
        await createCollection(collectionId, { token, useSchema: "crm" });
      }

      const toReturn = await getTokenInfo(data.token);
      const { id, firstName, lastName, email, permissions } = toReturn;
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify({ id, firstName, lastName, email, permissions }));
      rememberExpiry(data.expires); // LNK-5: so an expired login logs out instead of showing blank pages
      endAdminVisit();
      return toReturn;
    }
  } catch (error) {
    console.log("error", error);
  }
};

/**
 * Logs out the user with the provided token.
 * @param {Object} options - The logout options.
 * @param {string} options.token - The user's authentication token.
 * @returns {Promise<Object>} - A promise that resolves to the logout response data.
 */
export const logOutUser = async ({ token }) => {
  if (token) {
    // remove the token from the server
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const response = await fetch(`${API_DOMAIN}/sessions`, {
      method: "DELETE",
      headers: myHeaders,
      body: "",
      redirect: "follow",
    });

    const data = await response.json();
    console.log(data);

    localStorage.removeItem("user");
    localStorage.removeItem("token"); // log out (delete the token locally)
    forgetExpiry();
    endAdminVisit();

    return data;
  }
};

/**
 * Retrieves information about the user with the provided token.
 * @param {string|null} token - The user's authentication token.
 * @returns {Promise<Object|string>} - A promise that resolves to the user information if successful, or an error message if unsuccessful.
 */
export const getTokenInfo = async (token: string | null) => {
  if (token) {
    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    try {
      const resp = await fetch(`${API_DOMAIN}/sessions/${token}`, {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      });

      const response = await resp.json();
      // console.log('getTokenInfo', response);

      return response;
    } catch (error) {
      return error;
    }
  }
};

export const getSessionToken = (): string | null => {
  const token = localStorage.getItem("token");

  // redirect to login if no token
  if (!token) {
    // window.location.href = "/login";
    return null;
  }

  return token;

};

export const getBasicUserInfo = () => {
  const userString = localStorage.getItem("user");
  return userString ? JSON.parse(userString) : null;
}

export const isLoggedIn = async () => {
  const token = localStorage.getItem("token");
  if (!token) return false;

  const response = await getTokenInfo(token);
  if (has(response, 'error')) return false;

  return Boolean(keys(response).length)
}